import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { MemoSelectOption, SelectOption } from './SelectOption'

export const SelectTray = ({
  OptionComponent,
  searchString,
  options,
  optionGroups,
  hint,
  hintNoResults,
  onSelect,
  onChangeFocus,
  preselect,
  collapse,
  multiple,
  loading,
  withCheckbox,
  placeholder,
  openBottom,
  memoSelectOption
}) => {
  const [focusIndex, setFocusIndex] = useState(0)

  const elTray = useRef(null)
  const elOptions = useRef(null)

  const handleMouseEnter = useCallback(({ target: { dataset } }) => {
    const newFocusIndex = dataset.muOptionIndex
    if (newFocusIndex !== null && newFocusIndex !== undefined) {
      setFocusIndex(parseInt(newFocusIndex))
    }
  }, [])

  const scrollTrayToItem = useCallback(i => {
    if (!elOptions.current) {
      return
    }

    if (withCheckbox) {
      return
    }

    /*
    const elFocusedItem = elOptions.current.getElementsByClassName(
      'e-select__tray__option'
    )[Math.max(i - 1, 0)]
    */

    const elFocusedItem = elOptions.current.querySelector(
      `[data-mu-option-index="${i}"]`
    )

    elTray.current.scrollTop = elFocusedItem ? elFocusedItem.offsetTop : 0
  }, [])

  useEffect(() => {
    if (typeof onChangeFocus === 'function') {
      onChangeFocus(options[focusIndex])
    }
  }, [focusIndex])

  const onKeyDown = useCallback(
    e => {
      if (loading) {
        return
      }
      if (e.keyCode === 40) {
        // arrow down
        setFocusIndex(prevState => {
          const newFocusIndex = Math.min(prevState + 1, options.length - 1)
          scrollTrayToItem(newFocusIndex)
          return newFocusIndex
        })
      }
      if (e.keyCode === 38) {
        // arrow up
        setFocusIndex(prevState => {
          const newFocusIndex = Math.max(0, prevState - 1)
          scrollTrayToItem(newFocusIndex)
          return newFocusIndex
        })
      }

      if (e.keyCode === 27) {
        // escape
        collapse()
        return
      }

      if (e.keyCode === 13) {
        // enter
        setFocusIndex(() => {
          const newFocusIndex = 0
          return newFocusIndex
        })

        if (!multiple) {
          collapse()
        }
      }
    },
    [options]
  )

  useEffect(() => {
    /*
    scrollTrayToItem(0)
    setFocusIndex(() => {
      const newFocusIndex = +preselect

      scrollTrayToItem(newFocusIndex)
      return newFocusIndex
    })
    */

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [options, preselect])

  let groups
  let notGrouped = options

  if (optionGroups) {
    notGrouped = options.filter(
      option =>
        !Object.values(optionGroups)
          .reduce((r, { values }) => [...r, ...values], [])
          .includes(option.value)
    )

    groups = Object.entries(optionGroups).map(([id, { label, values }]) => ({
      id,
      label,
      options: options.filter(option => values.includes(option.value))
    }))
  }

  const MyOptionComponent = memoSelectOption
    ? MemoSelectOption
    : OptionComponent

  return (
    <div
      className={
        openBottom ? 'e-select__tray' : 'e-select__tray e-select__tray__openUp'
      }
      ref={elTray}
    >
      {!!options.length && (
        <ul className='e-select__tray__options' ref={elOptions}>
          {notGrouped.map((option, idx) => {
            return (
              <MyOptionComponent
                option={option}
                key={`option-${option.value}`}
                onMouseEnter={handleMouseEnter}
                onSelect={onSelect}
                optionIndex={idx}
                focus={idx === focusIndex}
                withCheckbox={withCheckbox}
                searchString={searchString}
                loading={loading}
              />
            )
          })}

          {groups &&
            groups.map(
              ({ id, options: groupOptions, label }) =>
                !!groupOptions.length && (
                  <li
                    key={`option-group-${id}`}
                    className='e-select__tray__group'
                  >
                    <strong className='e-select__tray__group-name'>
                      {label}
                    </strong>
                    <ul>
                      {groupOptions.map((option, idx) => {
                        const optionIndex = options.findIndex(
                          o => o.value === option.value
                        )
                        return (
                          <OptionComponent
                            option={option}
                            key={`option-${option.value}`}
                            onMouseEnter={handleMouseEnter}
                            onSelect={onSelect}
                            optionIndex={optionIndex}
                            focus={optionIndex === focusIndex}
                            withCheckbox={withCheckbox}
                            searchString={searchString}
                            loading={loading}
                          />
                        )
                      })}
                    </ul>
                  </li>
                )
            )}
        </ul>
      )}
      {hint && <p className='e-select__tray__hint'>{hint}</p>}
      {hintNoResults &&
        (placeholder ? options.length < 2 : options.length < 1) && (
          <p className='e-select__tray__hint'>{hintNoResults}</p>
        )}
    </div>
  )
}

SelectTray.defaultProps = {
  searchString: '',
  isOnlyText: true,
  memoSelectOption: false,
  OptionComponent: SelectOption
}

SelectTray.propTypes = {
  searchString: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    })
  ),
  onSelect: PropTypes.func,
  openBottom: PropTypes.bool,
  memoSelectOption: PropTypes.bool,
  OptionComponent: PropTypes.elementType
}
